import { JSX, useEffect } from 'react'

import { BellIcon } from '@Static/icons/FooterIcons'
import { ChartIcon } from '@Static/icons/HeaderNavigationIcons'
import cn from 'clsx'
import { Link, useLocation } from 'react-router-dom'

import localStorageManager from '@App/cookies/local_storage_manager'
import { useAppDispatch, useAppSelector } from '@App/hooks'
import { selectOpenedTradesAndOrdersCount } from '@App/redux/slices/trade/trades/selectors'
import { CalendarIcon } from '@App/static/icons/AccountMenuIcon'
import { ChartRiseUpIcon, PortfolioIcon, ReportIcon, SettingsIcon, ShareIcon } from '@App/static/icons/WalletPageIcons'

import { configFavoriteMarkets } from '@Redux/slices/config/config.selectors'
import { MODAL_TYPES } from '@Redux/slices/modals/enums'
import { setModal } from '@Redux/slices/modals/ModalSlice'
import { isLogin, userState } from '@Redux/slices/user/UserSlice'

import { ReportsTabs } from '@UI/crypto_exchange/desktop/pages/spot/components/Reports/model'
import { replaceVarsInPath, ROUTE_NAMES } from '@UI/crypto_exchange/desktop/routes/routeNames'

import s from './Header.module.scss'

interface ILink {
  name: string
  icon: JSX.Element
  link: string
  isPrivate?: boolean
}

const topHeaderLinks: ILink[] = [
  {
    name: 'Trade',
    icon: <ChartRiseUpIcon className={s.icon} />,
    link: ROUTE_NAMES.TRADING,
  },
  {
    name: 'Discover',
    icon: <ShareIcon className={s.icon} />,
    link: ROUTE_NAMES.DISCOVER,
  },
  {
    name: 'Charts',
    icon: <ChartIcon className={s.icon} />,
    link: ROUTE_NAMES.CHARTS,
  },
  {
    name: 'Portfolio',
    icon: <PortfolioIcon className={cn(s.icon, s.portfolioIcon)} />,
    link: ROUTE_NAMES.HISTORY,
    isPrivate: true,
  },
  {
    name: 'Reports',
    icon: <ReportIcon className={s.icon} />,
    link: `${replaceVarsInPath(ROUTE_NAMES.REPORTS, [ReportsTabs.Transactions])}`,
    isPrivate: true,
  },
  {
    name: 'Calendar',
    icon: <CalendarIcon className={cn(s.icon, s.calendarIcon)} />,
    link: ROUTE_NAMES.CALENDAR,
  },
  {
    name: 'All alerts',
    icon: <BellIcon className={cn(s.icon, s.alertIcon)} />,
    link: ROUTE_NAMES.ALL_ALERTS,
    isPrivate: true,
  },
]

export const HeaderNavigation = () => {
  const { theme } = useAppSelector((state) => userState(state))
  const location = useLocation()
  const dispatch = useAppDispatch()
  const login = useAppSelector((state) => isLogin(state))
  const favoriteMarkets = useAppSelector(configFavoriteMarkets)
  const openedTradesAndOrdersCount = useAppSelector(selectOpenedTradesAndOrdersCount)

  const checkActiveLink = (link: string) => {
    const activePathname = new URL(link, window.location.origin).pathname
    return location.pathname.split('/')[1] === activePathname.split('/')[1]
  }
  useEffect(() => {
    document.documentElement.dataset.theme = theme === 'dark' ? 'dark' : 'light'
    localStorageManager.set('theme', theme === 'dark' ? 'dark' : 'light')
  }, [theme])

  const openUserSettings = () => {
    dispatch(setModal({ modalType: MODAL_TYPES.USER_SETTINGS_MODAL }))
  }

  return (
    <nav className={s.leftMenuSideBar}>
      <div className={s.linkItems}>
        {topHeaderLinks.map((headerLink) => {
          if (headerLink.isPrivate && !login) return null
          return (
            <Link
              key={headerLink.name}
              className={cn(s.navLink, checkActiveLink(headerLink.link) && s.active)}
              to={headerLink.link}
            >
              <div className={s.iconContainer}>
                {headerLink.icon}
                {headerLink.name === 'Portfolio' && !!openedTradesAndOrdersCount ? (
                  <div className={s.favoriteMarkets}>{openedTradesAndOrdersCount}</div>
                ) : null}
                {headerLink.name === 'Charts' && favoriteMarkets.length ? (
                  <div className={s.favoriteMarkets}>{favoriteMarkets.length}</div>
                ) : null}
              </div>
              {headerLink.name}
            </Link>
          )
        })}
      </div>

      {login && (
        <div className={s.linkItems}>
          <Link onClick={openUserSettings} className={s.navLink} to={'#'}>
            <SettingsIcon className={s.icon} /> Settings
          </Link>
        </div>
      )}
    </nav>
  )
}
